import React, { useCallback, useEffect, useRef, useState } from "react";
import MuiAlert from '@mui/material/Alert';
import {
  Box,
  Grid,
  Snackbar,
  SnackbarCloseReason,
  Stack,
} from "@mui/material";
import "../../assets/style.css";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { useActions } from "../../hooks/useActions";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import EncompassService from "../../services/encompassService";
import { IConfiguration, IMessageType } from "../../models/configuration/pushback/IConfiguration";
import {
  ReceivedDate_Population_Items,
  GetPushbackFieldMappings,
  DefaultPushbackData,
  GetPushbackFieldNames
} from "../../constants/PushbackConstants";
import { AlertSeverity } from "../../constants/AlertTypes";
import DtForm from "../../components/ui/form/DtForm";
import { FormField } from "../../components/ui/form/formTypes";
import { DtPageHeader } from "../../components/ui/header";
import { DtCard } from "../../components/ui/card";
import { useDispatch } from "react-redux";
import { ActionType } from "../../state/actions";
import { hasPluginConfigChanges } from "../../utils/pluginConfigChangeDetector";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

const ENCOMPASS_INT_API_URI = process.env.REACT_APP_INTEGRATION_API;;
const ENCOMPASS_PLUGIN_API_URI = process.env.REACT_APP_DT_PLUGIN_API_URI;
const ENCOMPASS_TOKEN_ENDPOINT_API_URI =
  process.env.REACT_APP_DT_TOKEN_ENDPOINT_API_URI;

const ConfigPushback: React.FC = () => {
  //
  const { getConfigPushbackData, saveConfigPushbackData } =
    useActions();
  const { loading, data } = useTypedSelector(
    (state) => state.configPushbackDefaults
  );
  const pluginConfiguration = useTypedSelector((state) => state.configPlugInData.data?.Configuration);
  const persistedPluginConfiguration = useTypedSelector((state) => state.configPlugInData.persistedData?.Configuration);
  const xPathData = useTypedSelector((state) => state.xPathData.data);
  const persistedXPathData = useTypedSelector((state) => state.xPathData.persistedData);
  const documentBundles = useTypedSelector((state) => state.configDocumentBundles.data);
  const persistedDocumentBundles = useTypedSelector((state) => state.configDocumentBundles.persistedData);
  const eventBundles = useTypedSelector((state) => state.configEventBundles.data);
  const persistedEventBundles = useTypedSelector((state) => state.configEventBundles.persistedData);
  const dispatch = useDispatch();
  const [pushbackDefaultData, setPushbackDefaultData] =
    useState<IConfiguration | null>(data?.Configuration || null);
  const accessToken = useTypedSelector((state) => state.appSlice.accessToken);
  // Pushback state
  const [selectedConfigId, setSelectedConfigId] = useState<string>("");
  const [integrationUri, setIntegrationUri] = useState("");
  const [pluginUri, setPluginUri] = useState("");
  const [tokenUri, setTokenUri] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<IMessageType>({ Type: "", Message: [] });
  const pushBackFieldList = GetPushbackFieldMappings(DefaultPushbackData);
  const pushBackFieldNameList = GetPushbackFieldNames(DefaultPushbackData);
  const [alertSeverity, setAlertMessageSeverity] = useState<AlertSeverity>('info');
  const configId = EncompassService.getConfigId() as string;

  const hasFetchedData = useRef(false);

  const fetchPushbackData = useCallback(
    (configId: string) => {
      const result: any = getConfigPushbackData(configId, accessToken);
      result.catch((rejectMsg: IMessageType) => {
        handleAlert(rejectMsg, true, 'error')
      });
    },
    [getConfigPushbackData, accessToken]
  );

  const handleAlert = (message: IMessageType, open: boolean, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setAlertMessage(message);
    setAlertOpen(open);
    setAlertMessageSeverity(severity);
  }

  // Save Page Data
  const savePushbackConfigData = useCallback(
    async (configId: string, data: IConfiguration) => {
      return await saveConfigPushbackData(configId, data, accessToken);
    },
    [saveConfigPushbackData, accessToken]
  );

  useEffect(() => {
    if (!hasFetchedData.current && !data) {
      setSelectedConfigId(configId);
      const delayFetch = setTimeout(() => {
        fetchPushbackData(configId);
        initializeScreenData();
        hasFetchedData.current = true;
      }, 600);
      return () => clearTimeout(delayFetch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Data
  useEffect(() => {
    if (data != null) {
      setPushbackDefaultData(data?.Configuration); // Only update local data if there is no save Error condition
      initializeScreenData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.Configuration]);

  const updatePushbackData = (key: string, value: any) => {
    setPushbackDefaultData((prevData) => {
      if (!prevData) return null;
      const nextData = {
        ...prevData,
        [key]: value,
      };
      dispatch({
        type: ActionType.SET_CONFIG_PUSHBACK_LOCAL,
        payload: { Configuration: nextData }
      });
      return nextData;
    });
  };

  // Toast
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  function cacheEndpoints() {
    localStorage.setItem("integrationUri", integrationUri);
    localStorage.setItem("pluginUri", pluginUri);
    localStorage.setItem("tokenUri", tokenUri);
  }

  function initializeScreenData() {
    setIntegrationUri(
      localStorage.getItem("integrationUri") ||
      (ENCOMPASS_INT_API_URI as string)
    );
    setPluginUri(
      localStorage.getItem("pluginUri") || (ENCOMPASS_PLUGIN_API_URI as string)
    );
    setTokenUri(
      localStorage.getItem("tokenUri") ||
      (ENCOMPASS_TOKEN_ENDPOINT_API_URI as string)
    );
    if (!selectedConfigId) {
      setSelectedConfigId(getConfigId());
    }
  }

  const getConfigId = () => {
    if (selectedConfigId) return selectedConfigId;
    return EncompassService.getConfigId() as string;
  };

  const save = async () => {
    const globalSaved = consumeGlobalSaveHadChanges();
    cacheEndpoints();
    try {
      var result: any = await savePushbackConfigData(
        getConfigId(),
        pushbackDefaultData as IConfiguration
      );
      const normalizedType = typeof result?.Type === "string" ? result.Type.toLowerCase() : "success";
      const severity =
        normalizedType === "error"
          ? "error"
          : normalizedType === "info"
            ? "info"
            : "success";
      handleAlert(result, true, severity);
      const hasPluginChanges = pluginConfiguration && persistedPluginConfiguration
        ? hasPluginConfigChanges(pluginConfiguration, persistedPluginConfiguration)
        : false;
      const hasXPathChanges = xPathData && persistedXPathData
        ? hasXPathConfigChanges(xPathData, persistedXPathData)
        : false;
      const hasDocumentBundlingChange = documentBundles && persistedDocumentBundles
        ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles)
        : false;
      const hasEventBundlingChange = eventBundles && persistedEventBundles
        ? hasEventBundlingChanges(eventBundles, persistedEventBundles)
        : false;
      const hasOtherChanges = hasPluginChanges || hasXPathChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
      const isNoChanges = normalizedType === "info";
      if (hasOtherChanges && isNoChanges) {
        handleAlert({ Type: "success", Message: ["Successfully Saved Changes"] }, true, "success");
        return;
      }
    } catch (rejectMsg: any) {
      handleAlert(rejectMsg, true, 'error');
    }
  };

  // Register save function for header buttons
  useConfigurationSave(() => save());

  // Build your form fields array
  const fields: FormField[] = [
    // Group: Population Settings
    {
      id: "ReceivedDatePopulation",
      label: pushBackFieldList?.ReceivedDatePopulation,
      type: "select",
      options: ReceivedDate_Population_Items.map(item => ({ label: item, value: item })),
      size: 3,
      required: true,
      formGroup: "General",
      onChange(value, field) {
        updatePushbackData(field.id, value);
      },
    },
    {
      id: "UnnamedDocumentTitle",
      label: pushBackFieldList?.UnnamedDocumentTitle,
      type: "text",
      size: 5,
      required: true,
      formGroup: "General",
       onChange(value, field) {
        updatePushbackData(field.id, value);
      },
    },

    // Group: Concurrency & Retry
    {
      id: "MaxConcurrency",
      label: pushBackFieldList?.MaxConcurrency,
      type: "number",
      grid: {xs: 12, lg: 2},
      required: true,
      formGroup: "Concurrency & Retry",
       onChange(value, field) {
        updatePushbackData(field.id, value);
      },
    },
    {
      id: "MaxMessageRetryCount",
      label: pushBackFieldList?.MaxMessageRetryCount,
      type: "number",
      grid: {xs: 12, lg: 4, xl: 3},
      required: true,
      formGroup: "Concurrency & Retry",
       onChange(value, field) {
        updatePushbackData(field.id, value);
      },
    },

    {
      id: "MessageRetryVisibilityTimeoutSeconds",
      label: pushBackFieldList?.MessageRetryVisibilityTimeoutSeconds,
      type: "number",
      grid: {xs: 12, lg: 5 , xl: 4},
      required: true,
      formGroup: "Concurrency & Retry",
       onChange(value, field) {
        updatePushbackData(field.id, value);
      },
    },
    {
      id: "LoanLockedVisibilityTimeoutSeconds",
      label: pushBackFieldList?.LoanLockedVisibilityTimeoutSeconds,
      type: "number",
      grid: {xs: 12, lg: 6 , xl: 5},
      required: true,
      formGroup: "Concurrency & Retry",
       onChange(value, field) {
        updatePushbackData(field.id, value);
      },
    },
  ];

  return (
    <section>
      <div>
        {loading && (
          <ConfigLoad loading={loading} />
        )}
      </div>
      <Box component="main">
        <Stack direction='column' spacing={2}>
          <DtPageHeader>Manage Pushback Configuration</DtPageHeader>

            <Grid container justifyContent="start">
            <Grid item xs={12}>
                <DtForm
                  fields={fields}
                  values={pushbackDefaultData || {}}
                  onSubmit={data => {
                    setPushbackDefaultData(data as IConfiguration);
                    save();
                  }}
                  showSubmit={false}
                  showReset={false}
                  labelPosition="ontop"
                  gap={2}
                />
            </Grid>
          </Grid>

          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={alertOpen}
            autoHideDuration={4000}
            onClose={handleClose}
          >
            <MuiAlert
              onClose={handleClose}
              severity={alertSeverity}
              sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
              <div>
                {alertMessage?.Message?.[0]}
                {alertMessage?.Message?.length > 1 &&
                  <ul>
                    {alertMessage?.Message?.filter?.(
                      (val, index) => index !== 0 && val
                    ).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                }
              </div>
            </MuiAlert>
          </Snackbar>
        </Stack>
      </Box>
    </section>
  );
};

export default ConfigPushback;